import { GridApi } from "ag-grid-enterprise";
import { TColumnType, TSaveColumnState } from ".";

/** Synthetic `columnList` row: survives GraphQL input that has no top-level `rowGroupColIds`. */
export const ROW_GROUP_META_FIELD_NAME = "__ciq_row_group_meta__";

function splitRowGroupMetaFromColumnList(columnList: Array<TColumnType>): {
  columnList: Array<TColumnType>;
  rowGroupColIds: string[] | undefined;
  autoGroupColumnWidth: number | undefined;
} {
  const idx = columnList.findIndex(
    (c) => c.field.name === ROW_GROUP_META_FIELD_NAME
  );
  if (idx === -1) {
    return {
      columnList,
      rowGroupColIds: undefined,
      autoGroupColumnWidth: undefined
    };
  }
  const meta = columnList[idx];
  let rowGroupColIds: string[] | undefined;
  let autoGroupColumnWidth: number | undefined;
  try {
    const raw = meta.field.agFilter;
    if (raw) {
      const parsed = JSON.parse(raw) as {
        rowGroupColIds?: unknown;
        autoGroupColumnWidth?: unknown;
      };
      if (Array.isArray(parsed.rowGroupColIds)) {
        rowGroupColIds = parsed.rowGroupColIds.filter(
          (id): id is string => typeof id === "string"
        );
      }
      if (typeof parsed.autoGroupColumnWidth === "number") {
        autoGroupColumnWidth = Math.round(parsed.autoGroupColumnWidth);
      }
    }
  } catch {
    /* ignore invalid meta */
  }
  const next = columnList.filter((_, i) => i !== idx);
  return { columnList: next, rowGroupColIds, autoGroupColumnWidth };
}

function getAutoGroupColumnIds(api?: GridApi<any>): string[] {
  if (!api) return [];
  return (api.getColumnState?.() ?? [])
    .map((c) => c.colId)
    .filter(
      (id): id is string =>
        typeof id === "string" &&
        (id === "group_column" || id.startsWith("ag-Grid-AutoColumn"))
    );
}

function applyAutoGroupColumnWidth(api?: GridApi<any>, width?: number) {
  if (!api || typeof width !== "number") return;
  const autoGroupColIds = getAutoGroupColumnIds(api);
  if (!autoGroupColIds.length) return;
  api.applyColumnState({
    state: autoGroupColIds.map((colId) => ({ colId, width, flex: null })),
    applyOrder: false
  });
}

export const UtilColumnConfig = {
  getColumnStateFromGrid: (props: {
    gridRef?: {
      api: GridApi<any>;
    };
    isIncludeSearchText?: boolean;
    conditionals_formatting?: Array<any>;
  }) => {
    const {
      gridRef,
      isIncludeSearchText = true,
      conditionals_formatting = []
    } = props;
    const sortedColArr = [] as Array<TColumnType>;
    const colFilters = gridRef?.api?.getFilterModel();

    gridRef?.api?.getColumnState()?.forEach((col, index) => {
      const colKey = col.colId || "";
      const colDef = gridRef?.api?.getColumnDef(colKey);

      if (colFilters && col && colDef && !colDef.suppressColumnsToolPanel) {
        const agFilter = colFilters[colKey];

        const field = {
          name: colKey,
          agFilter: agFilter ? JSON.stringify(agFilter) : null,
          filter: null,
          sortOrder: col.sort || null,
          range: null,
          width: col?.width ? Math.round(col.width) : null
        };

        sortedColArr.push({
          order: index + 1,
          field,
          visibility: colDef?.lockVisible || !col.hide
        });
      }
    });

    const rowGroupColumns = gridRef?.api?.getRowGroupColumns?.() ?? [];
    const rowGroupColIds = rowGroupColumns
      .map((c: { getColId?: () => string }) =>
        typeof c.getColId === "function"
          ? c.getColId()
          : ((c as { colId?: string }).colId ?? "")
      )
      .filter(Boolean);
    const autoGroupColIds = getAutoGroupColumnIds(gridRef?.api);
    const autoGroupColumnWidth =
      (gridRef?.api?.getColumnState?.() ?? []).find((c) =>
        autoGroupColIds.includes(c.colId)
      )?.width ?? null;

    if (gridRef?.api) {
      sortedColArr.push({
        order: sortedColArr.length + 1,
        field: {
          name: ROW_GROUP_META_FIELD_NAME,
          agFilter: JSON.stringify({ rowGroupColIds, autoGroupColumnWidth }),
          filter: null,
          sortOrder: null,
          range: null,
          width: null
        },
        visibility: false
      });
    }

    return {
      columnList: sortedColArr,
      quickSearch: isIncludeSearchText
        ? (gridRef?.api?.getGridOption("quickFilterText") ?? "")
        : "",
      conditionals_formatting
    };
  },

  setFilterStateFromData: (props: {
    columnStates: TSaveColumnState;
    gridRef?: {
      api: GridApi<any>;
    };
    restoreRowGrouping?: boolean;
  }) => {
    const { columnStates, gridRef, restoreRowGrouping = true } = props;
    if (!columnStates) return;
    const { columnList: rawColumnList, quickSearch } = columnStates;
    const { columnList, rowGroupColIds, autoGroupColumnWidth } =
      splitRowGroupMetaFromColumnList(rawColumnList ?? []);
    gridRef?.api.setFilterModel(null);
    const filterstate: any = {};
    const checkBoxColumnId = "ag-Grid-SelectionColumn";

    gridRef?.api?.setGridOption("quickFilterText", quickSearch);

    if (columnList?.length > 0) {
      const columnState = gridRef?.api
        ?.getColumnState()
        ?.map((column: any) => {
          const colDef = gridRef?.api.getColumnDef(column.colId);
          if (colDef?.suppressColumnsToolPanel) return null;

          const colKey = column?.colId || "";
          if (colKey === checkBoxColumnId) {
            return { ...column, order: -1 }; // It Should be first
          }
          const col1 = columnList?.find((cols) => cols.field.name === colKey);

          if (!col1) {
            return { ...column, hide: true }; // Hide if not found in saved columns
          }

          const filter = col1?.field.filter;
          if (filter) {
            const range = { ...col1?.field?.range };
            // `range` is a fresh object literal spread, always truthy; false arm unreachable.
            /* v8 ignore next */
            if (range) {
              range.to = Number(range.to);
              range.from = Number(range.from);
            }
            filterstate[colKey] = {
              // Reached only inside `if (filter)`, so `filter` is truthy; `|| null` operand unreachable.
              /* v8 ignore next */
              values: filter || null,
              filterType: "set",
              range
            };
          } else if (col1?.field.agFilter) {
            filterstate[colKey] = JSON.parse(col1.field.agFilter);
          }

          const gridCol = { ...column };
          gridCol.hide = !colDef?.lockVisible && !col1?.visibility;
          gridCol.order = col1?.order;
          gridCol.sort = col1?.field.sortOrder;
          gridCol.width = col1?.field?.width;
          // A saved width must win over colDef flex, otherwise a resized column snaps back.
          if (typeof col1?.field?.width === "number") gridCol.flex = null;
          return gridCol;
        })
        .filter((c) => c);

      if (columnState) {
        const sortedArr = columnState?.sort((a, b) => a.order - b.order);
        gridRef?.api.applyColumnState({
          state: sortedArr,
          applyOrder: true
        });

        const hasFilters = Object.keys(filterstate).length > 0;
        const hasRowGroupRestore =
          restoreRowGrouping && rowGroupColIds !== undefined;

        if (hasFilters || hasRowGroupRestore) {
          setTimeout(() => {
            if (hasRowGroupRestore) {
              gridRef?.api?.setRowGroupColumns?.(rowGroupColIds);
            } else if (!restoreRowGrouping) {
              // Safeguard: ensure grouping is fully cleared when row grouping is disabled.
              gridRef?.api?.setRowGroupColumns?.([]);
            }
            // Auto group column can appear only after row grouping is applied.
            setTimeout(() => {
              applyAutoGroupColumnWidth(gridRef?.api, autoGroupColumnWidth);
            }, 0);
            if (hasFilters) {
              gridRef?.api?.setFilterModel(filterstate);
            } else {
              gridRef?.api.onFilterChanged();
            }
          }, 0);
        } else {
          gridRef?.api.onFilterChanged();
        }
      }
    }
  },

  setFilterStateFromDataForSubmittalLog: (props: {
    columnStates: TSaveColumnState;
    gridRef?: {
      api: GridApi<any>;
    };
  }) => {
    const { columnStates, gridRef } = props;
    if (!columnStates) return;
    const { columnList: rawColumnList, quickSearch } = columnStates;
    const { columnList, rowGroupColIds, autoGroupColumnWidth } =
      splitRowGroupMetaFromColumnList(rawColumnList ?? []);
    gridRef?.api.setFilterModel(null);
    const filterstate: any = {};
    const checkBoxColumnId = "ag-Grid-SelectionColumn";

    const defaultColIds = ["action"];

    gridRef?.api?.setGridOption("quickFilterText", quickSearch);

    if (columnList?.length > 0) {
      const columnState = gridRef?.api
        ?.getColumnState()
        ?.map((column: any) => {
          const colKey = column?.colId || "";
          if (colKey === checkBoxColumnId) {
            return { ...column, order: -1 }; // It Should be first
          }
          const colDef = gridRef?.api.getColumnDef(column.colId);
          const col1 = columnList?.find((cols) => cols.field.name === colKey);

          if (defaultColIds.includes(colKey)) {
            return { ...column, hide: false };
          }

          if (!col1) {
            return { ...column, hide: true };
          }

          const filter = col1?.field.filter;
          if (filter) {
            const range = { ...col1?.field?.range };
            // `range` is a fresh object literal spread, always truthy; false arm unreachable.
            /* v8 ignore next */
            if (range) {
              range.to = Number(range.to);
              range.from = Number(range.from);
            }
            filterstate[colKey] = {
              // Reached only inside `if (filter)`, so `filter` is truthy; `|| null` operand unreachable.
              /* v8 ignore next */
              values: filter || null,
              filterType: "set",
              range
            };
          } else if (col1?.field.agFilter) {
            filterstate[colKey] = JSON.parse(col1.field.agFilter);
          }

          const gridCol = { ...column };
          gridCol.hide = !colDef?.lockVisible && !col1?.visibility;
          gridCol.order = col1?.order;
          gridCol.sort = col1?.field.sortOrder;
          gridCol.width = col1?.field?.width;
          // A saved width must win over colDef flex, otherwise a resized column snaps back.
          if (typeof col1?.field?.width === "number") gridCol.flex = null;
          return gridCol;
        })
        .filter((c) => c);

      if (columnState) {
        const sortedArr = columnState?.sort((a, b) => a.order - b.order);
        gridRef?.api.applyColumnState({
          state: sortedArr,
          applyOrder: true
        });

        const hasFilters = Object.keys(filterstate).length > 0;
        const hasRowGroupRestore = rowGroupColIds !== undefined;

        if (hasFilters || hasRowGroupRestore) {
          setTimeout(() => {
            if (hasRowGroupRestore) {
              gridRef?.api?.setRowGroupColumns?.(rowGroupColIds);
            }
            // Auto group column can appear only after row grouping is applied.
            setTimeout(() => {
              applyAutoGroupColumnWidth(gridRef?.api, autoGroupColumnWidth);
            }, 0);
            if (hasFilters) {
              gridRef?.api?.setFilterModel(filterstate);
            } else {
              gridRef?.api.onFilterChanged();
            }
          }, 0);
        } else {
          gridRef?.api.onFilterChanged();
        }
      }
    }
  }
};
